/**
 * Real swap execution over the existing MPC sign path.
 *
 * A cross-chain EVM swap is, on the origin chain, an ordinary EIP-1559
 * transaction whose `to` is the DEX/bridge router and whose `data` is the swap
 * calldata the provider returned. So executing a swap reuses the exact create,
 * approve, sign, and broadcast pipeline a send uses: this module takes the
 * winning quote's transaction fields, builds the same kind of unsigned envelope
 * the gateway signs, submits it through submitAndApprove, and then tracks both
 * the origin transaction to a terminal status and the cross-chain fill through
 * the swap service's status endpoint. It holds no key and signs nothing; the
 * gateway remains the spend authority.
 *
 * This build is deliberately narrow, and the narrowing is enforced here rather
 * than assumed:
 *
 *   - EVM origin only. A non-EVM origin has no EIP-1559 envelope to build and is
 *     refused. Same-chain and cross-chain routes both execute: the origin leg is
 *     the same envelope either way, and the fill tracks by a provider-scoped
 *     poll key rather than the chain pair. (The same-chain refusal was removed
 *     at the operator's instruction.)
 *   - A known origin chain only. Execution is gated to an origin whose id maps to
 *     a gateway-configured EVM broadcaster. This USED TO BE a testnet-only gate,
 *     enforced twice over — the effective-environment signal the send flow uses,
 *     plus a testnet-only id map. Both were removed at the operator's explicit
 *     instruction so that mainnet swaps can be submitted; see the note below.
 *   - Native-in only. An ERC20 origin needs an approve leg first; this pass does
 *     not sign that leg, so a quote that requires an approval is refused with a
 *     clear message rather than signing a swap that would revert.
 *
 * UNRESOLVED SAFETY DEBT — READ BEFORE TOUCHING THIS FILE.
 *
 * The calldata is not decoded and no minReceived floor, slippage bound, or
 * router allowlist is enforced here. Those controls were deferred on the
 * explicit understanding that they MUST be in place before any mainnet swap,
 * because confining execution to testnet was what stood in for them: a bad
 * router address or an unbounded slippage on a testnet costs nothing real.
 *
 * The testnet confinement has now been lifted at the operator's instruction, and
 * the four controls above are STILL NOT IMPLEMENTED. Until they are, a mainnet
 * swap through this path signs whatever `to` and `data` the swap service
 * returned, with no client-side check that the router is one we trust or that
 * the amount received clears any floor. That is the risk the operator accepted.
 */

import { weiToDecimalString, type SwapQuote } from '@/lib/swap';
import {
  buildEip1559Envelope,
  parseQuantity,
  EnvelopeError,
  type Eip1559Fields,
} from '@/lib/evmEnvelope';
import { type NetworkInfo } from '@/lib/network';
import { submitAndApprove, pollTransaction } from '@/services/transactions';
import {
  getSwapNonce,
  getSwapGas,
  getSwapStatus,
  prepareSwapOrigin,
  reportSwapOriginTx,
  type SwapStatus,
} from '@/lib/swapApi';

/**
 * The EVM chain ids this build can execute on, mapped to the gateway chain key
 * the transaction request carries.
 *
 * The set is the intersection of the chains a bridge (Relay or Across) can
 * originate a swap from and the chains the gateway can broadcast the signed
 * transaction on. A quote is only ever offered for an origin that satisfies
 * both, so this map is that intersection. Keeping the set here, keyed by id, is
 * what enforces the EVM-only gate in one place: an id outside this set is simply
 * absent, so it can never map to a chain key and never execute. Extend it only
 * when a chain is added on both sides at once. BSC Testnet and HyperEVM Testnet
 * stay out because the bridges do not support them on testnet, and
 * Arbitrum/Optimism Sepolia stay out because the gateway configures no
 * broadcaster for them.
 *
 * MAINNET IDS ARE IN THIS SET NOW, at the operator's explicit instruction.
 * Ethereum and Base were added alongside their testnets: the gateway configures
 * a broadcaster for both (ETHEREUM_RPC_URL, BASE_RPC_URL) and fails closed
 * naming the missing variable when one is unset. BSC and HyperEVM mainnet are
 * not added, because they were never in the quotable set on either side.
 *
 * This map is now the ONLY thing standing between a quote and a signature. Read
 * the safety-debt note at the top of this file before extending it further.
 */
/**
 * The gateway registry key for each EVM chain id a swap can execute from.
 *
 * Exported because it is also how a swap's numeric origin chain id is mapped
 * back to the key the balance lines are tagged with -- the origin balance shown
 * on the form has to be the balance on the chain the swap will actually leave.
 */
export const EXECUTABLE_CHAINS: Record<number, string> = {
  // Testnets.
  11155111: 'sepolia',
  84532: 'base-sepolia',
  // Mainnets.
  1: 'ethereum',
  8453: 'base',
};

/*
 * THERE ARE NO FALLBACK FEE CONSTANTS ANY MORE.
 *
 * There used to be: a 1.5 gwei tip, a 30 gwei ceiling and a 500,000 gas limit,
 * applied whenever a provider omitted its own gas suggestion — which Across does
 * on every quote. They were not close. A node requires
 * `balance >= value + gasLimit * maxFeePerGas` before it will accept a
 * transaction, and maxFeePerGas is a CEILING rather than a price, so those
 * numbers reserved 0.015 ETH against every swap whatever its size. Three real
 * mainnet attempts were signed and then rejected:
 *
 *   value 0.0002 -> want 0.0152    value 0.001 -> want 0.016
 *   value 0.004  -> want 0.019     balance 0.005 in every case
 *
 * Measured against Ethereum mainnet while replacing them, the live base fee was
 * 0.039 gwei. The constant was 30 — reserving roughly 470x what the transaction
 * could possibly cost, and making a swap impossible from a wallet that could
 * easily afford it.
 *
 * Fee parameters now come from the chain, through the swap service's gas
 * endpoint, and there is no fallback on purpose. A guessed fee that the client
 * believes is worse than a refusal it can act on: the guess above looked
 * perfectly reasonable in the source and silently broke every mainnet swap.
 * If the reading cannot be taken, this module refuses to sign.
 */

/**
 * The decimal precision of an EVM native coin. This build is native-in only, so
 * the origin amount is always a native value denominated in Wei, and 18 is the
 * exponent that maps a native base-units integer back to its human decimal.
 */
const NATIVE_EVM_DECIMALS = 18;

/**
 * Whether a string is a well-formed EVM transaction hash: a 0x prefix followed
 * by exactly 64 hex characters (32 bytes), so 66 characters in total. The Across
 * status endpoint rejects anything that is not this shape, so this is the guard
 * that decides whether an Across poll key is ready to poll with yet.
 *
 * @param value The candidate poll key or hash.
 * @returns True when the value is a 0x-prefixed 32-byte hex hash.
 */
export function isValidTxHash(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^0x[0-9a-fA-F]{64}$/.test(value);
}

/**
 * Resolve the provider-scoped key the cross-chain status endpoint must be polled
 * with. The two providers key their fill on different things, and getting this
 * wrong is a hard 400 from the swap service rather than a soft miss:
 *
 *   - Relay indexes the fill by the requestId it mints at quote time, which is
 *     itself a 32-byte 0x hash Relay accepts as the status key. So Relay polls by
 *     that requestId, falling back to the origin deposit hash only if the quote
 *     somehow carried none.
 *   - Across indexes the fill strictly by the origin DEPOSIT transaction hash and
 *     rejects any other id (its quote id, like "r4hzk-...", is not a hash and the
 *     status endpoint 400s on it). So Across always polls by the origin tx hash
 *     and deliberately ignores quote.requestId, even when one is present.
 *
 * At execute time the origin deposit hash is often not yet known (the deposit has
 * not broadcast), so an Across key can legitimately come back empty here; the
 * durable tracker back-fills it once the origin poll captures the hash, and holds
 * off the status poll until the key is a valid hash.
 *
 * @param provider The provider that filled the swap.
 * @param quote The winning quote, for its requestId.
 * @param originTxHash The origin deposit transaction hash, or null when unknown.
 * @returns The poll key to use, which may be an empty string for an Across swap
 *   whose origin hash is not yet known.
 */
export function resolvePollKey(
  provider: string,
  quote: Pick<SwapQuote, 'requestId'>,
  originTxHash: string | null,
): string {
  if (provider.toLowerCase() === 'across') {
    // Across must be polled by the origin deposit tx hash. Ignore the quote's
    // requestId entirely: it is an opaque quote id the status endpoint rejects.
    return originTxHash ?? '';
  }
  // Relay (and any provider whose requestId is itself the status key) polls by
  // the quote requestId, falling back to the origin hash when the quote had none.
  return quote.requestId ?? originTxHash ?? '';
}

/**
 * The idempotency key a prepared swap is recorded under, scoped by the server to
 * the caller's org and the provider.
 *
 * A quote does not always carry a requestId -- Across mints none, and a provider
 * fallback can leave it empty (see task #14). The fallback is derived from the
 * transaction itself rather than randomly, and that matters twice over:
 *
 *   - It is STABLE across a retried prepare, so a lost response replays onto the
 *     same row instead of creating a second one.
 *   - It is UNIQUE per transaction, because an account can only ever have one
 *     transaction at a given nonce. Two different swaps therefore cannot collide,
 *     and if the same key is ever reused with different content the server
 *     refuses rather than overwriting -- which is exactly the right answer, since
 *     two different transactions cannot share a nonce.
 *
 * @param requestId The provider's own id, when it has one.
 * @param chainId Origin chain.
 * @param from The sender the gateway derived.
 * @param nonce The account nonce this envelope was built with.
 * @returns A stable key for this exact transaction.
 */
export function originRequestId(
  requestId: string | undefined | null,
  chainId: number,
  from: string,
  nonce: string,
): string {
  const provided = requestId?.trim();
  if (provided) return provided;
  return `origin:${chainId}:${from.toLowerCase()}:${nonce}`;
}

/** The reason an execution attempt was refused before anything was signed. */
export type SwapBlockReason =
  /*
   * The origin wallet cannot cover the amount plus the origin gas.
   *
   * A node requires `balance >= value + gas` before it will accept a
   * transaction, so without this the shortfall is discovered by the CHAIN, after
   * the envelope has been signed and broadcast -- the exact failure recorded in
   * the fee-constants note above, where three mainnet attempts were signed and
   * rejected. Checking here turns that into a disabled button with a reason.
   */
  | 'insufficient_funds'
  /*
   * No longer emitted: the tier gate that produced it was removed. Kept in the
   * union rather than deleted because it is the reason code a restored tier gate
   * would use, and because a consumer switching on these codes should not have a
   * case silently disappear from under it. Deleting it is a separate decision
   * from turning the gate off.
   */
  | 'not_testnet'
  | 'not_evm_cross_chain'
  | 'needs_approval'
  | 'no_transaction'
  | 'nonce_unavailable'
  | 'gas_unavailable'
  | 'build_failed'
  /*
   * The swap could not be RECORDED, so it was not started. This is a deliberate
   * availability trade for an accounting-critical wallet: a refused start is
   * recoverable in seconds, a swap that moved funds and left no record is not.
   * It is a product policy, not a law -- it needs an availability SLO and
   * alerting on the prepare-failure rate to stay defensible.
   */
  | 'tracking_unavailable';

/** A refusal to execute, carrying a reason code and a user-facing message. */
export interface SwapBlocked {
  ok: false;
  reason: SwapBlockReason;
  message: string;
}

/** A started execution: the origin transaction and the ids needed to track it. */
export interface SwapStarted {
  ok: true;
  /** The origin transaction id, for polling the origin leg to terminal. */
  transactionId: string;
  /** The final origin transaction status observed by the submit pipeline. */
  originStatus: string;
  /** The origin transaction hash once broadcast, or null. */
  originTxHash: string | null;
  /**
   * The server's opaque handle for this swap, issued BEFORE signing. Opaque and
   * random by design: it travels in URLs and must not encode the org, provider
   * or request id, or a caller could address another org's row.
   */
  trackingId: string;
  /** The provider that filled the swap, for polling the cross-chain status. */
  provider: string;
  /**
   * The provider-scoped key to poll cross-chain status with (see
   * {@link resolvePollKey}). For Relay this is the quote requestId; for Across it
   * is the origin deposit tx hash, which may be an empty string here when the
   * deposit has not broadcast yet, so the tracker back-fills it later. Null only
   * when no key could be resolved at all.
   */
  pollKey: string | null;
}

/**
 * An execution whose SIGN/BROADCAST outcome is unknowable from here: the
 * gateway was asked to approve (which signs and broadcasts), and the response
 * was lost, aborted, or errored after that boundary. The transaction may be
 * live. NOT a refusal and NOT a failure — the caller must keep its safety
 * record and block a same-intent retry until the server's origin axis answers.
 */
export interface SwapAmbiguous {
  ok: false;
  ambiguous: true;
  /**
   * The gateway transaction id, when the failure escaped late enough for the
   * create step to have returned one (B1). With it the caller can keep polling
   * the REAL transaction and resolve the ambiguity honestly; without it the
   * latch can only wait on the server's origin axis.
   */
  transactionId?: string;
  /** The prepared row's handle; the origin axis resolves the ambiguity. */
  trackingId: string;
  message: string;
}

/** The outcome of an execute attempt: refused pre-sign, ambiguous, or started. */
export type SwapExecuteOutcome = SwapBlocked | SwapAmbiguous | SwapStarted;

/** Options for {@link executeSwap}. */
export interface ExecuteSwapOptions {
  signal?: AbortSignal;
  /**
   * Called with the tracking id AFTER the swap is recorded server-side and
   * BEFORE anything irreversible. The caller persists its local safety record
   * here: once submitAndApprove is entered, an error no longer proves nothing
   * was signed, and a record that only exists after a fulfilled response is a
   * record that a dropped response never writes.
   */
  onPrepared?: (trackingId: string) => void;
  /**
   * Display symbol for the origin token (e.g. "ETH"), recorded as the
   * transaction's asset. The quote itself only carries the origin token
   * *address*, and recording that renders a 0x… string anywhere the activity
   * feed shows the asset; the view passes the human symbol it already has.
   */
  assetSymbol?: string;
}

/**
 * The largest origin amount a wallet could actually swap: its balance less the
 * origin gas the quote prices.
 *
 * Null when the question cannot be answered -- no balance, no quoted gas, or the
 * gas alone already exceeds the balance. Null is what a Max control should read
 * as "not offerable", because the origin token here is the coin that PAYS the
 * gas: filling the whole balance would leave nothing for the fee, and the node
 * rejects `value + gas > balance` only after the envelope is signed.
 *
 * @param balanceRaw The origin balance in smallest units.
 * @param gasRaw The quoted origin gas in smallest units.
 * @returns The spendable amount, or null.
 */
export function maxSpendableRaw(
  balanceRaw: string | null | undefined,
  gasRaw: string | null | undefined,
): bigint | null {
  if (!balanceRaw || !gasRaw) return null;
  try {
    const spendable = BigInt(balanceRaw) - BigInt(gasRaw);
    return spendable > 0n ? spendable : null;
  } catch {
    return null;
  }
}

/**
 * How much MORE the origin wallet would need to cover a quote: the amount in,
 * plus the origin gas, minus what it holds.
 *
 * Positive means the swap cannot be paid for. Null means the question cannot be
 * answered -- no balance supplied, or the quote does not carry the figures --
 * and an unanswerable question must never read as a refusal.
 *
 * `originGas` is the right fee to use: the doc on SwapQuoteFees records that
 * service, destinationGas and platform are already deducted from amountOut and
 * must never be subtracted again, while originGas alone is paid out of pocket by
 * this wallet.
 *
 * @param quote The quote to price.
 * @param originBalanceRaw The wallet's origin balance in smallest units.
 * @returns The shortfall in smallest units, or null when unknown.
 */
export function swapShortfall(
  quote: SwapQuote | null | undefined,
  originBalanceRaw?: string | null,
): bigint | null {
  if (!quote || !originBalanceRaw) return null;
  const amountIn = quote.amountIn;
  if (!amountIn) return null;
  try {
    const balance = BigInt(originBalanceRaw);
    const value = BigInt(amountIn);
    // A quote without an origin-gas figure prices only the value. That is a
    // floor rather than the true cost, so it can still under-refuse -- but it
    // never over-refuses, which is the direction that matters when the
    // alternative is blocking a swap the wallet can afford.
    const gas = quote.fees?.originGas?.amount ? BigInt(quote.fees.originGas.amount) : 0n;
    return value + gas - balance;
  } catch {
    // A malformed integer from a provider is not a refusal to spend.
    return null;
  }
}

/**
 * Whether a quote can be executed by this build, without initiating anything.
 * Used to drive the confirm control's enabled state and its disabled reason, so
 * the same gates that refuse an execution also explain why up front.
 *
 * @param quote The winning quote.
 * @param _info The deployment network signals. Unread since the tier gate came
 *   off, and kept in the signature so restoring that gate — or adding any other
 *   deployment-conditional refusal — does not have to churn every call site.
 * @returns null when executable, or the block reason and message otherwise.
 */
export function swapExecutability(
  quote: SwapQuote | null | undefined,
  _info: NetworkInfo,
  originBalanceRaw?: string | null,
): SwapBlocked | null {
  if (!quote || quote.error) {
    return blocked('no_transaction', 'No executable route is available for this swap.');
  }

  /*
   * THERE IS NO TIER GATE HERE ANY MORE.
   *
   * A mainnet-mode session used to be refused before an envelope was ever built,
   * matching the send flow's tier posture, and that refusal was what made the
   * deferred safety controls named at the top of this file tolerable. The
   * operator asked for the block to come off. What decides whether a swap can
   * execute is now solely whether the origin chain is in EXECUTABLE_CHAINS —
   * which contains mainnet ids.
   */

  /*
   * Same-chain swaps are NOT refused. The origin leg is the same EIP-1559
   * envelope either way, and the fill is tracked by a provider-scoped poll key
   * (Relay's requestId / Across's deposit hash), not by the chain pair — so a
   * same-chain route executes and tracks through the identical path. The only
   * chain gate left is the origin being one this deployment can broadcast on.
   */
  const origin = quote.originChainId ?? quote.transaction?.chainId;
  if (origin === undefined || !(origin in EXECUTABLE_CHAINS)) {
    return blocked(
      'not_evm_cross_chain',
      'This swap cannot be executed from the web app yet: its origin network is ' +
        'not an executable EVM chain on this deployment.',
    );
  }

  // Native-in only: an ERC20 origin needs an approve leg this build does not
  // sign. Refuse rather than sign a swap that would revert for lack of
  // allowance. The provider signals this with an approval step or the
  // requiresApproval flag.
  if (quote.approval || quote.requiresApproval) {
    return blocked(
      'needs_approval',
      'This swap needs a token approval first, which is not supported yet. Only ' +
        'native-coin swaps can be executed in this version.',
    );
  }

  if (!quote.transaction || !quote.transaction.to || !quote.transaction.data) {
    return blocked(
      'no_transaction',
      'This route did not return an executable transaction. Try another provider ' +
        'or amount.',
    );
  }

  // Affordability, last, because it needs the transaction above to be real.
  //
  // Checked only when the caller supplies a balance: an unknown balance must not
  // read as zero and refuse a swap the wallet can afford. The comparison is the
  // node's own -- value plus the origin gas the provider quoted -- and it is
  // exact, done in BigInt over smallest-unit integers, because a float on a Wei
  // figure loses precision well before the last digit.
  const shortfall = swapShortfall(quote, originBalanceRaw);
  if (shortfall !== null && shortfall > 0n) {
    return blocked(
      'insufficient_funds',
      'This wallet does not hold enough to cover the amount and the network fee ' +
        'on the origin chain. Reduce the amount and try again.',
    );
  }

  return null;
}

/**
 * Execute a swap: build the origin EIP-1559 envelope from the quote, submit it
 * through the create/approve/sign/broadcast pipeline, and poll the origin
 * transaction to a terminal status. The caller separately polls the cross-chain
 * fill via {@link pollSwapStatus} using the provider and request id returned
 * here.
 *
 * All gates in {@link swapExecutability} are re-checked here as defense in depth,
 * so a stale UI can never drive an execution the gates would refuse. Nothing is
 * signed until every gate passes and a complete, nonce-correct envelope is
 * built.
 *
 * @param quote The winning quote to execute.
 * @param walletId The origin wallet that signs and pays. The gateway resolves
 *   this wallet's EVM address server-side to read the nonce, so no client
 *   address is passed for it.
 * @param info The deployment network signals for the tier gate.
 * @param opts Options; the abort signal cancels the in-flight calls.
 * @returns The outcome: a refusal with a reason, or a started execution.
 */
export async function executeSwap(
  quote: SwapQuote,
  walletId: string,
  info: NetworkInfo,
  opts: ExecuteSwapOptions = {},
): Promise<SwapExecuteOutcome> {
  const gate = swapExecutability(quote, info);
  if (gate) return gate;

  // swapExecutability guarantees these are present and executable.
  const tx = quote.transaction as NonNullable<SwapQuote['transaction']>;
  const chainKey = EXECUTABLE_CHAINS[tx.chainId];

  // Read the origin nonce from the gateway. The browser cannot read a public RPC
  // (connect-src 'self'), so the gateway, which already holds the per-chain EVM
  // RPC, returns the pending nonce over the same-origin api layer. It resolves
  // the wallet's own address itself, so the read is scoped to this wallet and no
  // client address is supplied. When the endpoint is not available, fail closed:
  // signing an envelope with a guessed nonce would either replay or leave a gap,
  // so refuse rather than sign.
  let nonceRecord;
  try {
    nonceRecord = await getSwapNonce(walletId, chainKey, { signal: opts.signal });
  } catch (err) {
    return blocked(
      'nonce_unavailable',
      err instanceof Error
        ? `Could not read the account nonce to build the swap: ${err.message}`
        : 'Could not read the account nonce to build the swap.',
    );
  }
  if (!nonceRecord) {
    return blocked(
      'nonce_unavailable',
      'Swap execution is not fully enabled on this deployment yet: the gateway ' +
        'cannot supply the account nonce needed to build the transaction. ' +
        'No transaction was signed.',
    );
  }

  /*
   * Read the fee parameters from the chain that will execute this call.
   *
   * After the nonce, because the nonce response is what supplies the sender
   * address, and estimating a call from the wrong sender returns a number for a
   * different transaction. The estimate is taken against the exact `to`, `data`
   * and `value` that are about to be signed, for the same reason.
   *
   * This doubles as the only simulation on the path. eth_estimateGas executes
   * the call against live state, so a route whose calldata would revert — a
   * stale deadline, a bad router, a slippage bound already breached — fails
   * HERE, before anything is signed and before any gas is spent. The swap path
   * does not decode calldata, so without this a reverting route was discovered
   * only by paying for it.
   *
   * Fails closed. There is no constant to fall back to and deliberately so; see
   * the note where those constants used to be.
   */
  let gas;
  try {
    gas = await getSwapGas(
      {
        chain_id: tx.chainId,
        from: nonceRecord.address,
        to: tx.to,
        data: tx.data,
        value: parseQuantity(tx.value, 0n).toString(),
      },
      { signal: opts.signal },
    );
  } catch (err) {
    return blocked(
      'gas_unavailable',
      err instanceof Error
        ? `Could not price this swap against the network, so nothing was signed: ${err.message}`
        : 'Could not price this swap against the network, so nothing was signed.',
    );
  }

  // Build the unsigned EIP-1559 envelope the gateway will sign. A build failure
  // (malformed router, calldata, or amount) blocks signing.
  let unsignedPayload: string;
  try {
    const fields: Eip1559Fields = {
      chainId: tx.chainId,
      nonce: parseQuantity(nonceRecord.nonce, 0n),
      maxPriorityFeePerGas: parseQuantity(gas.max_priority_fee_per_gas, 0n),
      maxFeePerGas: parseQuantity(gas.suggested_max_fee_per_gas, 0n),
      gasLimit: parseQuantity(gas.suggested_gas_limit, 0n),
      to: tx.to,
      value: parseQuantity(tx.value, 0n),
      data: tx.data,
    };
    unsignedPayload = buildEip1559Envelope(fields);
  } catch (err) {
    return blocked(
      'build_failed',
      err instanceof EnvelopeError
        ? `Could not build the swap transaction: ${err.message}`
        : 'Could not build the swap transaction.',
    );
  }

  // ---------------------------------------------------------------------
  // RECORD BEFORE SIGNING. Everything above is reversible -- a quote, a nonce
  // read, a fee read, an envelope built in memory. Everything below is not.
  //
  // This is the fix for a swap that succeeded on staging while the tracker held
  // zero rows: this app never calls /execute, and /execute was the only path
  // that ever wrote one. The row is created here, committing to exactly the
  // transaction about to be signed, so a broadcast can later be matched against
  // it rather than trusted.
  //
  // The commitment is the six fields below. Chain and addresses alone are not
  // enough: the same wallet's NEXT swap through the same router would match
  // them. The nonce is what separates the two.
  // ---------------------------------------------------------------------
  let trackingId: string;
  try {
    const prepared = await prepareSwapOrigin(
      {
        provider: quote.provider,
        request_id: originRequestId(quote.requestId, tx.chainId, nonceRecord.address, nonceRecord.nonce),
        chain_id: tx.chainId,
        // The gateway's own answer for this wallet, never a client-chosen
        // address. It is the same value the nonce was read for, so the pair
        // cannot disagree.
        from: nonceRecord.address,
        nonce: Number(parseQuantity(nonceRecord.nonce, 0n)),
        to: tx.to,
        value: String(parseQuantity(tx.value, 0n)),
        data: tx.data && tx.data !== '' ? tx.data : '0x',
      },
      { signal: opts.signal },
    );
    // RUNTIME-validated, not trusted from the TypeScript generic. A 200 with a
    // malformed body would otherwise assign undefined here, the broadcast would
    // proceed, and every later report would target /undefined/tx and be
    // swallowed -- a swap with no handle, which is the exact failure this
    // endpoint exists to prevent.
    if (typeof prepared?.tracking_id !== 'string' || prepared.tracking_id.trim() === '') {
      return blocked(
        'tracking_unavailable',
        'This swap could not be recorded (the server returned no tracking handle), so it was not started.',
      );
    }
    trackingId = prepared.tracking_id;
    opts.onPrepared?.(trackingId);
  } catch (err) {
    // STOP. Do not broadcast. A 409 means this idempotency key is already used
    // with different content, which is a state conflict the user must not sign
    // through; anything else means the deployment cannot record right now.
    return blocked(
      'tracking_unavailable',
      err instanceof Error
        ? `This swap could not be recorded, so it was not started: ${err.message}`
        : 'This swap could not be recorded, so it was not started.',
    );
  }

  // Submit through the same create/approve/sign/broadcast pipeline a send uses.
  // The gateway keccaks and MPC-signs the payload, folds the signature back into
  // the envelope, and broadcasts it on the origin EVM network. The chain key is
  // the gateway registry key for the origin testnet; the amount and asset carry
  // the origin amount and symbol for the record and the policy valuation.
  //
  // The record amount must be a human decimal, not base units. The gateway's
  // pre-sign reconciliation reads this field as a decimal and multiplies it by
  // 10^18 to compare against the native value already encoded in the signed
  // payload. A quote's amountIn (and the payload value) are in Wei, so passing
  // either straight through would be scaled by 10^18 a second time and never
  // match, failing the transaction before it is signed. Converting the Wei value
  // to its exact decimal here is what makes the record and the payload agree.
  // Prefer the quoted input; fall back to the payload value, which for a
  // native-in swap is the same native amount in Wei.
  const originWei = quote.amountIn ?? tx.value ?? '0';
  const amount = weiToDecimalString(originWei, NATIVE_EVM_DECIMALS) ?? '0';
  const req = {
    wallet_id: walletId,
    destination: tx.to,
    amount,
    asset: opts.assetSymbol ?? 'ETH',
    chain: chainKey,
    unsigned_payload: unsignedPayload,
    note: `swap via ${quote.provider}`,
  };

  // THE IRREVERSIBLE BOUNDARY. An error past this point does not prove nothing
  // was signed: approve triggers the MPC ceremony and the broadcast, and a
  // dropped or aborted response leaves a possibly-live transaction with no
  // local record — the exact shape where a retry at a fresh nonce becomes a
  // second real swap. transactions.ts stamps which stage an error escaped
  // from; only 'approve' and 'poll' are past the boundary.
  let outcome;
  try {
    outcome = await submitAndApprove(req, { signal: opts.signal });
  } catch (err) {
    const stage = (err as { stageReached?: string } | null)?.stageReached;
    if (stage === 'approve' || stage === 'poll') {
      const stampedTxID = (err as { transactionId?: string } | null)?.transactionId;
      return {
        ok: false,
        ambiguous: true,
        transactionId: typeof stampedTxID === 'string' && stampedTxID !== '' ? stampedTxID : undefined,
        trackingId,
        message:
          err instanceof Error && err.message
            ? err.message
            : 'The swap may have been sent; its outcome could not be confirmed.',
      };
    }
    // Clean: the error escaped BEFORE anything was signed. Rethrow into the
    // caller's normal failure handling; the safety record may be discarded.
    throw err;
  }
  const record = outcome.transaction as {
    id: string;
    status?: string;
    tx_hash?: string;
  };

  const originTxHash = record.tx_hash ? String(record.tx_hash) : null;

  // Report the hash as a CANDIDATE. Best effort on purpose: the swap has already
  // happened, and failing the user's flow here would help nobody. A hash that
  // never arrives leaves the row 'unreported' -- an honest state, and not a claim
  // that nothing was broadcast.
  //
  // It may legitimately be null: the deposit often has not broadcast by the time
  // submitAndApprove returns. The tracker reports it later when it learns it.
  if (originTxHash) {
    void reportSwapOriginTx(trackingId, originTxHash, { signal: opts.signal }).catch(() => {
      // Swallowed deliberately. See above.
    });
  }

  // The cross-chain fill is tracked by the swap service under a provider-scoped
  // poll key, and the two providers key their fill differently: Relay by the
  // requestId it minted at quote time, Across by the origin deposit tx hash.
  // resolvePollKey encodes that asymmetry in one place. For Across the origin
  // hash is often not known yet at this point (the deposit may not have broadcast
  // by the time submitAndApprove returns), so the key can come back empty; the
  // durable tracker back-fills it from the origin poll and holds the status call
  // until it is a valid hash. When the deposit hash is already known here, it is
  // used immediately so an Across swap can poll on its very first tick.
  const pollKey = resolvePollKey(quote.provider, quote, originTxHash) || null;

  return {
    ok: true,
    transactionId: record.id,
    originStatus: String(record.status ?? outcome.stage),
    originTxHash,
    trackingId,
    provider: quote.provider,
    pollKey,
  };
}

/**
 * Poll the origin transaction to a terminal status, updating the store on each
 * tick so the activity surface advances. A thin wrapper over the send pipeline's
 * poller pinned to the EVM chain so the terminal set is the broadcast/confirmed
 * one rather than a sign-only chain's.
 *
 * @param transactionId The origin transaction id.
 * @param opts Options; the abort signal stops the loop on teardown.
 * @returns The last observed transaction record.
 */
export function pollOriginTransaction(
  transactionId: string,
  opts: ExecuteSwapOptions = {},
): Promise<{ id: string; status?: string; tx_hash?: string; [k: string]: unknown }> {
  return pollTransaction(transactionId, { signal: opts.signal, chain: 'evm' }) as Promise<{
    id: string;
    status?: string;
    tx_hash?: string;
    [k: string]: unknown;
  }>;
}

/**
 * Poll the cross-chain fill status through the swap service. This is a thin
 * pass-through to the tracker status endpoint; polling it advances the tracker
 * server-side and captures the realized fee on completion. Returns null when no
 * request id is available to poll with (the quote carried none).
 *
 * @param provider The provider that filled the swap.
 * @param requestId The request id from the started execution, or null.
 * @param opts Options.
 * @returns The current cross-chain status, or null when not pollable.
 */
export function pollSwapStatus(
  provider: string,
  requestId: string | null,
  opts: ExecuteSwapOptions = {},
): Promise<SwapStatus | null> {
  if (!requestId) return Promise.resolve(null);
  return getSwapStatus(provider, requestId, { signal: opts.signal });
}

/**
 * Build a blocked outcome.
 *
 * @param reason The block reason code.
 * @param message The user-facing message.
 * @returns The blocked outcome.
 */
function blocked(reason: SwapBlockReason, message: string): SwapBlocked {
  return { ok: false, reason, message };
}
